/*
 * Note: This reducer breaks convention on how reducers should be setup.
 */
import IErrorState from "./models/IErrorState";
import IAction from "../../models/IAction";
import * as ErrorAction from "./ErrorAction";
import HttpErrorResponseModel from "../../models/HttpErrorResponseModel";
import { Reducer, UnknownAction } from "redux";
import { isIAction } from "../../utilities/ReduxUtility";

export const initialState: IErrorState = {};

const errorReducer: Reducer<IErrorState, UnknownAction> = (
  state: IErrorState = initialState,
  action: UnknownAction,
): IErrorState => {
  if (!isIAction(action)) {
    return state;
  }

  const { type, error, payload } = action as IAction<any>;

  /*
   * Removes an HttpErrorResponseModel by it's id that is in the action payload.
   */
  if (type === ErrorAction.REMOVE) {
    // Create a new state without the error that has the same id as the payload.
    const newState: { [key: string]: HttpErrorResponseModel } = {};
    Object.entries(state).forEach(([key, value]: [string, HttpErrorResponseModel]) => {
      if (value.id !== payload) {
        Object.assign(newState, { [key]: value });
      }
    });
    return newState as IErrorState;
  }

  /*
   * Removes all errors by returning the initial state which is an empty object.
   */
  if (type === ErrorAction.CLEAR_ALL) {
    return initialState;
  }

  /*
   * True if the action type has the key word '_FINISHED' then the action is finished.
   */
  const isFinishedRequestType = type.includes("_FINISHED");
  /*
   * True if the action type has the key word 'REQUEST_' and not '_FINISHED'.
   */
  const isStartRequestType = type.includes("REQUEST_") && !isFinishedRequestType;

  /*
   * If an action is started we want to remove any old errors because there is a new action has been re-dispatched.
   */
  if (isStartRequestType) {
    // Using ES7 Object Rest Spread operator to omit properties from an object.
    const { [`${type}_FINISHED`]: _value, ...stateWithoutFinishedType } = state;

    return stateWithoutFinishedType;
  }

  /*
   * True if the action is finished and the error property is true.
   */
  const isError: boolean = isFinishedRequestType && Boolean(error);

  /*
   * For any start and finished actions that don't have errors we return the current state.
   */
  if (isError === false) {
    return state;
  }

  /*
   * At this point the "type" will be a finished action type (e.g. "SomeAction.REQUEST_*_FINISHED").
   * The payload will be a HttpErrorResponseModel.
   */
  return {
    ...state,
    [type]: payload,
  };
};

export default errorReducer;
